// React
import { useEffect, useState } from "react";

// Third party imports
import { useDispatch, useSelector } from "react-redux";

// Redux
import { setLiteSelectedCompany, clearLiteSelectedCompany } from "redux/slices/companiesSlice.jsx";
import { setError } from "redux/slices/snackbarSlice.jsx";

// API
import { getCompanyLiteData } from "api/companies.jsx";

/**
 * Custom hook to fetch company lite data
 * @param {string} companyId - Company ID
 * @param {boolean} enabled - Whether the fetch should be enabled
 * @returns {Object} Object containing company lite data, loading state, and error
 */
const useCompanyLite = (companyId, enabled = true) => {
  const dispatch = useDispatch();

  // Redux selectors
  const liteSelectedCompany = useSelector((state) => state.companies.liteSelectedCompany);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Track if we've already fetched to prevent duplicates
  const [fetchedCompanyId, setFetchedCompanyId] = useState(null);

  /**
   * Fetch company lite data from API
   */
  const fetchCompanyLite = async () => {
    if (!companyId || !enabled) return;

    // Prevent duplicate requests for the same company
    if (fetchedCompanyId === companyId && liteSelectedCompany?.id === companyId) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await getCompanyLiteData(companyId);
      dispatch(setLiteSelectedCompany(response.data));
      setFetchedCompanyId(companyId);
    } catch (err) {
      dispatch(setError({
        message: err.response?.data?.message || "Failed to fetch company data",
        severity: "error"
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch company data when companyId changes
  useEffect(() => {
    if (companyId && enabled) {
      fetchCompanyLite();
    }
  }, [companyId, enabled]);

  // Clear selected company when component unmounts
  useEffect(() => {
    return () => {
      if (!enabled) {
        dispatch(clearLiteSelectedCompany());
      }
    };
  }, [enabled, dispatch]);

  // Reset fetched ID when companyId changes
  useEffect(() => {
    if (companyId !== fetchedCompanyId) {
      setFetchedCompanyId(null);
    }
  }, [companyId, fetchedCompanyId]);

  return {
    liteSelectedCompany,
    isLoading,
  };
};

export default useCompanyLite;
